/**
 * Phira player settings popover (W2-02; DESIGN.md §4.8): what players with other Phira settings see, labelled with
 * Phira's names, preview-only and never written to the chart: aspect ratio override, note size, double hint
 * (simultaneous-note highlight), particles, aggressive culling, strict autoplay hit colours, MSAA, the HUD, and a
 * background dim override. Values are remembered per user (settings.preview.player, ext) and mirrored into the
 * editor store, which the frame options read.
 *
 * The popover is controlled by the stage state (`phiraOpen`), so the `preview.phiraSettings` command opens it from
 * the palette, a menu or a shortcut. The palette and menus hand focus back as they close (after the command ran),
 * and that focus move would read as "focus left the popover" and close it at once. So when the command ran from such
 * a non-pointer source, the stage state carries a grace start (`phiraGraceAt`): a focus move out of the popover
 * within OPEN_GRACE_MS of it is undone (focus goes back into the popover). A pointer press outside the popover ends
 * the grace at once (the click is the user going elsewhere, e.g. the stage or an inspector field), and opening with
 * the trigger has none. Escape, clicks outside, the trigger and later focus moves close it as usual.
 */

import { useId, useLayoutEffect, useRef } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { PhiraPlayerSettings } from '../../core/engine/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import { DEFAULT_PLAYER, normalizePlayer } from '../../state/editorStore';
import { Button } from '../../ui/Button';
import { IconButton } from '../../ui/IconButton';
import { Popover } from '../../ui/Popover';
import { Select } from '../../ui/Select';
import { Slider } from '../../ui/Slider';
import { Switch } from '../../ui/Switch';
import { runCommand, useEditorValue, useExt, useStoreValue } from './hooks';
import { m } from './preview.messages';
import { aspectName, bgDimOf, EXT_BG_DIM } from './prefs';
import { stageClock, stageStateOf } from './stageState';
import s from './Preview.module.css';
import { PREVIEW_FRAME_ID } from '../../render/types';

const ASPECTS: readonly (readonly [string, number | null])[] = [
  ['chart', null],
  ['16:9', 16 / 9],
  ['16:10', 16 / 10],
  ['3:2', 3 / 2],
  ['4:3', 4 / 3],
  ['19.5:9', 19.5 / 9],
  ['21:9', 21 / 9],
];

const chartAspectOf = (sv: AppServices): number => sv.store?.doc.pkg.aspectRatio ?? 16 / 9;
const chartDimOf = (sv: AppServices): number => sv.store?.doc.pkg.backgroundDim ?? 0.6;

/**
 * A focus move out of the popover this soon after a non-pointer opening is the opener's focus restore (ms; see the
 * header).
 */
export const OPEN_GRACE_MS = 600;
const TRIGGER = '[data-toggle="preview.phiraSettings"]';
const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The popover surface (the dialog around the content). */
const surfaceOf = (content: HTMLElement): Element => content.closest('[role="dialog"]') ?? content;

/**
 * Whether a close request is the opener handing focus back right after a non-pointer opening: the grace is running
 * (no pointer press outside since) and focus is now outside the popover surface, not on its trigger. Escape and
 * pointer-down-outside leave focus inside, or end the grace first.
 */
function isFocusRestore(content: HTMLElement | null, graceAt: number | null): boolean {
  if (content === null || graceAt === null || stageClock() - graceAt > OPEN_GRACE_MS) return false;
  const active = typeof document === 'undefined' ? null : document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return false;
  if (surfaceOf(content).contains(active)) return false;
  return active.closest(TRIGGER) === null;
}

/** Applies a player-settings patch to the user settings (remembered) and the editor store (drawn). */
export function setPlayer(services: AppServices, patch: Partial<PhiraPlayerSettings>): void {
  const next = normalizePlayer(patch, services.editor.getState().preview.player);
  services.settings.set('preview', 'player', next);
  services.editor.getState().setPreview({ player: next });
  services.scheduler.request(PREVIEW_FRAME_ID);
}

export interface PhiraSettingsPopoverProps {
  readonly services: AppServices;
  /** Controlled open state (the `preview.phiraSettings` command opens it). */
  readonly open: boolean;
  /** Start of the opener's focus grace (stage state `phiraGraceAt`; null = none). */
  readonly graceAt: number | null;
}

export function PhiraSettingsPopover(props: PhiraSettingsPopoverProps) {
  const { services, open, graceAt } = props;
  const t = useT();
  const titleId = useId();
  const player = useEditorValue(services, (st) => st.preview.player);
  const showHud = useEditorValue(services, (st) => st.preview.showHud);
  const chartAspect = useStoreValue(services, chartAspectOf);
  const chartDim = useStoreValue(services, chartDimOf);
  const dimOverride = useExt(services, EXT_BG_DIM, bgDimOf);
  const aspectKey = player.aspectOverride === null ? 'chart' : aspectName(player.aspectOverride);
  const known = ASPECTS.some(([k]) => k === aspectKey);
  const label: MessageRef = { key: m.cmdPhiraSettings };
  const contentRef = useRef<HTMLDivElement>(null);

  // While the grace runs, a pointer press outside the popover ends it: the focus move that press causes (the stage
  // focuses its panel, a field takes focus) is the user going elsewhere, not the opener's hand-back. Capture phase,
  // so it runs before the pressed element moves focus and before Radix's outside-press handling; registered in the
  // commit that opens the popover, so no press slips through before it.
  useLayoutEffect(() => {
    if (!open || graceAt === null || typeof document === 'undefined') return undefined;
    const stage = stageStateOf(services);
    const onPointerDown = (e: Event): void => {
      const content = contentRef.current;
      if (content !== null && e.target instanceof Node && surfaceOf(content).contains(e.target)) return;
      if (stage.getSnapshot().phiraGraceAt === graceAt) stage.set({ phiraGraceAt: null });
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [services, open, graceAt]);

  const onOpenChange = (o: boolean): void => {
    // The live snapshot: a pointer press earlier in this same event may have ended the grace.
    const grace = stageStateOf(services).getSnapshot().phiraGraceAt;
    if (!o && open && isFocusRestore(contentRef.current, grace)) {
      // Keep it open and take focus back (after the focus event that asked to close has finished).
      setTimeout(() => {
        const content = contentRef.current;
        if (content === null) return;
        (content.querySelector<HTMLElement>(FOCUSABLE) ?? content).focus({ preventScroll: true });
      }, 0);
      return;
    }
    stageStateOf(services).set({ phiraOpen: o, phiraGraceAt: null });
  };

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      side="bottom"
      align="end"
      label={t(m.phiraTitle)}
      trigger={
        <IconButton icon="Settings2" size="sm" label={label} pressed={open} data-toggle="preview.phiraSettings" />
      }
    >
      <div ref={contentRef} className={s.phira} aria-labelledby={titleId} tabIndex={-1}>
        <h3 id={titleId} className={s.phiraTitle}>
          {t(m.phiraTitle)}
        </h3>
        <p className={s.phiraNote}>{t(m.phiraNote)}</p>
        <Select<string>
          label={t(m.phiraAspect)}
          value={known ? aspectKey : 'chart'}
          options={ASPECTS.map(([k]) => ({
            value: k,
            label: k === 'chart' ? t(m.phiraAspectChart, { aspect: aspectName(chartAspect) }) : k,
          }))}
          onChange={(v) => {
            const hit = ASPECTS.find(([k]) => k === v);
            setPlayer(services, { aspectOverride: hit?.[1] ?? null });
          }}
        />
        <Slider
          label={t(m.phiraNoteScale)}
          value={player.noteScale}
          min={0.5}
          max={2}
          step={0.05}
          format={(v) => t(m.phiraNoteScaleValue, { value: v.toFixed(2) })}
          onChange={(v) => {
            setPlayer(services, { noteScale: v });
          }}
        />
        <Switch
          label={t(m.phiraDoubleHint)}
          checked={player.doubleHint}
          onChange={(v) => {
            setPlayer(services, { doubleHint: v });
          }}
        />
        <Switch
          label={t(m.phiraParticles)}
          checked={player.particles}
          onChange={(v) => {
            setPlayer(services, { particles: v });
          }}
        />
        <Switch
          label={t(m.phiraAggressive)}
          checked={player.aggressiveCull}
          onChange={(v) => {
            setPlayer(services, { aggressiveCull: v });
          }}
        />
        <Switch
          label={t(m.phiraStrictAutoplay)}
          checked={player.strictAutoplayHitColor}
          onChange={(v) => {
            setPlayer(services, { strictAutoplayHitColor: v });
          }}
        />
        <Switch
          label={t(m.phiraMsaa)}
          checked={player.msaa}
          onChange={(v) => {
            setPlayer(services, { msaa: v });
          }}
        />
        <Switch
          label={t(m.phiraApFc)}
          checked={player.apFcIndicator ?? true}
          onChange={(v) => {
            setPlayer(services, { apFcIndicator: v });
          }}
        />
        <Switch
          label={t(m.cmdHud)}
          checked={showHud}
          onChange={() => {
            runCommand(services, 'preview.toggleHud');
          }}
        />
        <Switch
          label={t(m.phiraDimOverride, { value: Math.round(chartDim * 100) })}
          checked={dimOverride !== null}
          onChange={(v) => {
            services.settings.setExt(EXT_BG_DIM, v ? chartDim : null);
            services.scheduler.request(PREVIEW_FRAME_ID);
          }}
        />
        {dimOverride !== null && (
          <Slider
            label={t(m.phiraDim)}
            value={dimOverride}
            min={0}
            max={1}
            step={0.05}
            format={(v) => t(m.percentValue, { value: Math.round(v * 100) })}
            onChange={(v) => {
              services.settings.setExt(EXT_BG_DIM, v);
              services.scheduler.request(PREVIEW_FRAME_ID);
            }}
          />
        )}
        <Button
          size="sm"
          variant="secondary"
          icon="RotateCcw"
          onClick={() => {
            setPlayer(services, DEFAULT_PLAYER);
            services.settings.setExt(EXT_BG_DIM, null);
          }}
        >
          {t(m.phiraReset)}
        </Button>
      </div>
    </Popover>
  );
}
